import { useState, useEffect } from 'react';
import { X, Cookie, Shield, BarChart3, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * CookieConsent - GDPR compliant cookie consent banner
 * Manages consent for: Necessary (always on), Analytics, Marketing
 */

// Google Consent Mode v2 update – uses window.gtag defined in public/index.html
let lastConsentSignal = '';
const pushConsentUpdate = (consentData) => {
  if (typeof window.gtag !== 'function') return;
  const ads = consentData.marketing ? 'granted' : 'denied';
  const analytics = consentData.analytics ? 'granted' : 'denied';
  const signal = `${ads}|${analytics}`;
  if (signal === lastConsentSignal) return; // avoid duplicate pushes (e.g. StrictMode double effects)
  lastConsentSignal = signal;
  window.gtag('consent', 'update', {
    'ad_storage': ads,
    'ad_user_data': ads,
    'ad_personalization': ads,
    'analytics_storage': analytics
  });
};

const CookieConsent = ({ onConsentChange }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true, // Always true
    analytics: false,
    marketing: false
  });

  // Check for existing consent on mount
  useEffect(() => {
    // Don't show cookie banner on admin pages - it blocks login
    const isAdminPage = window.location.pathname.startsWith('/admin');
    
    const savedConsent = localStorage.getItem('cookie_consent');
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        pushConsentUpdate(parsed);
        onConsentChange?.(parsed);
      } catch (e) {
        // Invalid consent, show banner (but not on admin)
        if (!isAdminPage) {
          setShowBanner(true);
        }
      }
    } else {
      // No consent saved, show banner (but not on admin)
      if (!isAdminPage) {
        setShowBanner(true);
      }
    }
  }, [onConsentChange]);
  const saveConsent = (newConsent) => {
    const consentData = {
      ...newConsent,
      necessary: true, // Always true
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setConsent(consentData);
    pushConsentUpdate(consentData);
    onConsentChange?.(consentData);
    setShowBanner(false);
    setShowDetails(false);
  };

  // Accept all cookies
  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true
    });
  };

  // Accept only necessary
  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false
    });
  };

  // Save custom selection
  const saveSelection = () => {
    saveConsent(consent);
  };

  // Open settings (called from footer link)
  const openSettings = () => {
    setShowBanner(true);
    setShowDetails(true);
  };

  // Expose openSettings globally for footer link
  useEffect(() => {
    window.openCookieSettings = openSettings;
    return () => {
      delete window.openCookieSettings;
    };
  }, []);

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        data-testid="cookie-consent-banner"
      >
        {/* Header */}
        <div className="bg-[#1C1C2E] text-white p-6 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <Cookie className="w-8 h-8 text-[#C8963E]" />
            <div>
              <h2 className="text-xl font-serif font-bold">Cookie-instellingen</h2>
              <p className="text-sm text-gray-300">Uw privacy is belangrijk voor ons</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {!showDetails ? (
            <>
              <p className="text-[#2C2C3E] text-sm leading-relaxed">
                Wij gebruiken cookies om uw ervaring te verbeteren, websiteverkeer te analyseren 
                en gepersonaliseerde advertenties te tonen. U kunt uw voorkeuren aanpassen.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={acceptAll}
                  className="flex-1 bg-[#C8963E] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#B8862E] transition-colors"
                  data-testid="cookie-accept-all"
                >
                  Alles accepteren
                </button>
                <button
                  onClick={acceptNecessary}
                  className="flex-1 bg-gray-200 text-[#2C2C3E] py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                  data-testid="cookie-reject-optional"
                >
                  Alleen noodzakelijk
                </button>
              </div>
              
              <button
                onClick={() => setShowDetails(true)}
                className="w-full text-[#C8963E] text-sm font-medium hover:underline"
                data-testid="cookie-show-details"
              >
                Instellingen aanpassen
              </button>
            </>
          ) : (
            <>
              {/* Detailed settings */}
              <div className="space-y-4">
                {/* Necessary cookies */}
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <Shield className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#2C2C3E]">Noodzakelijk</h3>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Altijd aan</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Essentieel voor het functioneren van de website. Omvat sessie- en cookievoorkeuren.
                    </p>
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#2C2C3E]">Analytisch</h3>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={consent.analytics}
                          onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                          className="sr-only peer"
                          data-testid="cookie-analytics-toggle"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#C8963E]"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Google Analytics en Tag Manager om websiteverkeer te meten en te verbeteren.
                    </p>
                  </div>
                </div>

                {/* Marketing cookies */}
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <Megaphone className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#2C2C3E]">Marketing</h3>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={consent.marketing}
                          onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                          className="sr-only peer"
                          data-testid="cookie-marketing-toggle"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#C8963E]"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Facebook Pixel en Google Ads om advertenties te personaliseren en conversies te meten.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={saveSelection}
                  className="flex-1 bg-[#C8963E] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#B8862E] transition-colors"
                  data-testid="cookie-save-selection"
                >
                  Opslaan
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 bg-[#1C1C2E] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#2C2C3E] transition-colors"
                >
                  Alles accepteren
                </button>
              </div>
            </>
          )}

          {/* Links */}
          <div className="flex justify-center gap-4 pt-2 text-xs text-gray-500">
            <Link to="/privacy" className="hover:text-[#C8963E] hover:underline">Privacybeleid</Link>
            <span>|</span>
            <Link to="/cookies" className="hover:text-[#C8963E] hover:underline">Cookiebeleid</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Get current consent status
 */
export const getCookieConsent = () => {
  try {
    const saved = localStorage.getItem('cookie_consent');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    // Invalid consent
  }
  return null;
};

/**
 * Check if analytics cookies are allowed
 */
export const isAnalyticsAllowed = () => {
  const consent = getCookieConsent();
  return consent?.analytics === true;
};

/**
 * Check if marketing cookies are allowed
 */
export const isMarketingAllowed = () => {
  const consent = getCookieConsent();
  return consent?.marketing === true;
};

export default CookieConsent;
